'use client'

import { useTranslations } from 'next-intl'
import { Check } from 'lucide-react'
import type { Step } from './stepMachine'

/**
 * Indicador de progreso del checkout (spec 2026-09-03 §4.6). No es navegable:
 * el flujo ya tiene sus botones. Un paso queda "completado" (check gris) si
 * ya se pasó por él en el orden normal (`i < currentIdx`) O si está en
 * `completed` — pasos saltados por estar resueltos (la dirección con
 * cotización viva). Son dos fuentes independientes: el cliente tiene que ver
 * TANTO por dónde ya pasó COMO lo que se resolvió solo, no solo lo segundo.
 *
 * El orden de los elementos en el DOM (círculo, luego etiqueta) es el MISMO
 * que el orden visual — nada de reordenar con CSS `order`: un lector de
 * pantalla anuncia en orden de DOM, así que visual y accesible deben coincidir.
 */
export interface StepProgressProps {
  steps: Step[]
  current: Step
  completed: ReadonlySet<Step>
  accentColor: string
}

const LABEL_KEY: Record<Exclude<Step, 'confirm'>, string> = {
  cart: 'progressCart',
  fulfillment: 'progressFulfillment',
  when: 'progressWhen',
  address: 'progressAddress',
  customer: 'progressCustomer',
  tip: 'progressTip',
  donation: 'progressDonation',
  payment: 'progressPayment',
}

export function StepProgress({ steps, current, completed, accentColor }: StepProgressProps) {
  const t = useTranslations('public.onlineOrder.checkout')
  const visible = steps.filter((s): s is Exclude<Step, 'confirm'> => s !== 'confirm')
  const currentIdx = visible.indexOf(current as Exclude<Step, 'confirm'>)

  return (
    <ol className="flex items-center justify-center gap-2 px-5 pt-1 pb-2">
      {visible.map((s, i) => {
        const isCurrent = s === current
        const isDone = completed.has(s) || i < currentIdx
        return (
          <li
            key={s}
            className="flex items-center gap-1.5 text-[11px] font-bold"
            aria-current={isCurrent ? 'step' : undefined}
            data-completed={isDone && !isCurrent ? 'true' : undefined}
            style={{ color: isCurrent ? accentColor : isDone ? '#374151' : '#9CA3AF' }}
          >
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px]"
              style={{
                backgroundColor: isCurrent ? accentColor : isDone ? '#374151' : 'transparent',
                border: isCurrent || isDone ? 'none' : '1.5px solid #D1D5DB',
                color: '#fff',
              }}
            >
              {isDone && !isCurrent ? <Check className="w-2.5 h-2.5" strokeWidth={3} /> : i + 1}
            </span>
            <span>{t(LABEL_KEY[s])}</span>
            {i < visible.length - 1 && <span className="w-4 h-px bg-gray-200 ml-0.5" aria-hidden />}
          </li>
        )
      })}
    </ol>
  )
}
